import { Box, Divider, Stack, Typography } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "../../language";
import { useTour } from '../../components/Tour/useTour';
import { HelpCircle } from 'lucide-react';
import ButtonsMain from '../../components/Button/ButtonMain';
import WelcomeGlassAccordion from './WelcomeGlassAccordion';

function NumberedItem({ number, text, accentColor, isLast }) {
  const theme = useTheme();
  return (
    <>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ py: 1.5 }}>
        {/* Number badge */}
        <Box
          sx={{
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: `2px solid ${accentColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: '2px',
          }}
        >
          <Typography
            variant="number4"
            sx={{
              color: accentColor,
              fontWeight: 700,
              fontSize: '0.75rem',
              lineHeight: 1,
            }}
          >
            {number}
          </Typography>
        </Box>

        {/* Text */}
        <Typography variant="subtitle1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {text}
        </Typography>
      </Stack>
      {!isLast && (
        <Divider
          sx={{
            borderColor: theme.palette.mode === 'light' ? 'grey.C900' : 'greydark.C400',
            mx: 0,
          }}
        />
      )}
    </>
  );
}

export default function WelcomePage() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { restart } = useTour();
  const tourAccent = theme.palette.tertiary.C300;

  const items = [
    {
      key: 'intro',
      title: t('welcome_section_header'),
      body: t('welcome_vision'),
      extra: (
        <Box>
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1, lineHeight: 1.7 }}>
            {t('welcome_pattern_intro')}
          </Typography>
          {['welcome_pattern_item1', 'welcome_pattern_item2', 'welcome_pattern_item3'].map((key, i, arr) => (
            <NumberedItem
              key={key}
              number={i + 1}
              text={t(key)}
              accentColor={tourAccent}
              isLast={i === arr.length - 1}
            />
          ))}
        </Box>
      ),
    },
    {
      key: 'tour',
      title: t('welcome_tour_header'),
      body: t('welcome_tour_prompt'),
      extra: (
        <ButtonsMain
          name={t('tour_restart_label')}
          action={restart}
          startIcon={<HelpCircle size={18} />}
          sx={{
            border: `2px solid ${tourAccent}`,
            borderColor: tourAccent,
            borderRadius: '999px',
            '@keyframes tourPulse': {
              '0%, 100%': { boxShadow: `0 0 0 0 ${alpha(tourAccent, 0)}` },
              '55%':       { boxShadow: `0 0 0 10px ${alpha(tourAccent, 0.22)}` },
            },
            animation: 'tourPulse 2.2s ease-in-out infinite',
            // This button spans almost the full card width, so ButtonsMain's
            // default hover scale(1.2) blows it out past the rounded card
            // edges — drop the scale here, keep everything else.
            '&:hover': { transform: 'none' },
          }}
        />
      ),
    },
    {
      key: 'aging',
      title: t('welcome_aging_header'),
      body: [t('welcome_aging_text'), t('welcome_aging_text2')],
    },
    {
      key: 'privacy',
      title: t('welcome_privacy_header'),
      body: t('welcome_privacy_text'),
    },
    {
      key: 'help',
      title: t('welcome_help_header'),
      body: t('welcome_help_text'),
    },
  ];

  return (
    <Box sx={{ width: '100%', maxWidth: { xs: 1100, sm: 700 }, mx: 'auto', px: { xs: 2, sm: 4 }, pt: { xs: 4, sm: 6 }, pb: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <ButtonsMain
          name={t('tour_restart_label')}
          action={restart}
          startIcon={<HelpCircle size={16} />}
        />
      </Box>
      <WelcomeGlassAccordion items={items} />
    </Box>
  );
}
